<script setup>
import { computed } from 'vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import ProjectDot from '@/components/project/ProjectDot.vue'
import { resolveTeamMember, teamRoleKey, teamRoleLabel } from '@/Utils/teamAccessSummary'

/**
 * Which workspaces a person is on, as chips: a color dot and the workspace name.
 * The role there is in the tooltip; a paused grant is dimmed and says so. The
 * workspace the page is standing in is outlined so "here" reads at a glance.
 */
const props = defineProps({
	params: {
		type: Object,
		default: null,
	},
	member: {
		type: Object,
		default: null,
	},
	// The workspace the Team page is standing in; its chip is outlined.
	activeProjectId: {
		type: String,
		default: '',
	},
	// How many chips to show before folding the rest into "+N".
	limit: {
		type: Number,
		default: 3,
	},
	// Card meta line: tighter chips.
	compact: {
		type: Boolean,
		default: false,
	},
})

const data = computed(() => resolveTeamMember(props))
const isOwner = computed(() => teamRoleKey(data.value.role) === 'owner')
const isPending = computed(() => data.value.status === 'pending')

const workspaces = computed(() => (Array.isArray(data.value.workspaces) ? data.value.workspaces : []))
const visible = computed(() => workspaces.value.slice(0, props.limit))
const overflow = computed(() => workspaces.value.slice(props.limit))

const describe = (workspace) => {
	if (isPending.value) return `Invited to ${workspace.name} as ${teamRoleLabel(workspace.role) || 'Moderator'}`
	if (workspace.isEnabled === false) return `Paused in ${workspace.name}`
	return `${teamRoleLabel(workspace.role) || 'Moderator'} in ${workspace.name}`
}

const chipClass = (workspace) => [
	'inline-flex items-center gap-1.5 rounded-full bg-surface-3 max-w-full',
	props.compact ? 'px-1.5 py-px text-[11px]' : 'px-2 py-0.5 text-tiny',
	workspace.id && workspace.id === props.activeProjectId ? 'ring-1 ring-surface-5' : '',
	workspace.isEnabled === false ? 'opacity-60' : '',
]
</script>

<template>
	<div class="flex flex-wrap items-center gap-1 min-w-0" :class="compact ? '' : 'h-full content-center'">
		<span v-if="isOwner" class="text-surface-7" :class="compact ? 'text-[11px]' : 'text-tiny'">All workspaces</span>
		<span v-else-if="!workspaces.length" class="text-surface-6" :class="compact ? 'text-[11px]' : 'text-tiny'">No workspaces</span>
		<template v-else>
			<Tooltip
				v-for="workspace in visible"
				:key="workspace.id || workspace.name"
				:content="describe(workspace)"
				wrapper-class="inline-flex max-w-full"
				wrap-trigger
			>
				<span :class="chipClass(workspace)" :data-testid="`project-chip-${workspace.id}`">
					<ProjectDot :color="workspace.color" size="size-1.5" />
					<span class="truncate text-surface-text-1">{{ workspace.name }}</span>
					<span v-if="workspace.isEnabled === false" class="shrink-0 text-surface-6">· Paused</span>
				</span>
			</Tooltip>
			<Tooltip v-if="overflow.length" wrapper-class="inline-flex" interactive>
				<span
					class="inline-flex items-center rounded-full bg-surface-3 text-surface-7 tabular-nums"
					:class="compact ? 'px-1.5 py-px text-[11px]' : 'px-2 py-0.5 text-tiny'"
				>+{{ overflow.length }}</span>
				<template #content>
					<ul class="m-0 list-none space-y-0.5 p-0">
						<li v-for="workspace in overflow" :key="workspace.id || workspace.name">{{ describe(workspace) }}</li>
					</ul>
				</template>
			</Tooltip>
		</template>
	</div>
</template>
